import {
  ArchiveBoxIcon,
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  FireIcon,
  HomeIcon,
  PresentationChartBarIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/solid';
import { PaginatedSaleList } from '@qogita/canary-types';
import { Navigation } from '@qogita/ui';
import { useQueryClient } from '@tanstack/react-query';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { useUserContext } from '#contexts/User';
import { getMappedTabForStatus } from '#src/components/shared/SearchBar/SearchBarHits';
import { isProduction } from '#src/constants/featureFlags';
import { useGetSale } from '#src/hooks/api/useGetSale';
import { useIsInsightsEnabled } from '#src/hooks/featureFlags/useIsInsightsEnabled';
import { getOrdersQueryKeyPartial } from '#src/utils/queryKey';
import { routes } from '#utils/routes';

type SellerSidebarProps = {
  validateIsActiveLink: (testPath: RegExp) => boolean;
  onClickRoute?: () => void;
};

/**
 * Returns the tab that should be active for the order QID page.
 *
 * This function will check the sale status in 2 ways:
 * 1. If the sale is in the single sale cache (from the individual sale page)
 * 2. If the sale is in the list cache (from the list pages)
 *
 * The reason we need to check both is to show the correct status when navigating
 * from the list pages to the individual sale page, and have a smooth transition
 * between the both.
 *
 * After getting the sale, will return the tab for the sale status.
 */
function useActiveOrderTabForOrderQidPage(isInOrdersPath: boolean) {
  const router = useRouter();
  const { qid } = router.query;

  const queryClient = useQueryClient();

  const isInOrderQidPath = isInOrdersPath && Boolean(qid);

  const { data: sale } = useGetSale({
    saleQid: String(qid),
    enabled: isInOrderQidPath,
  });

  if (!isInOrderQidPath) {
    return undefined;
  }

  // If we have a sale, return the tab for its status
  if (sale) {
    return getMappedTabForStatus(sale.status);
  }

  // If we don't have a sale, but we're in the order path,
  // the individual sale is still loading, but it is probably
  // in the cache from the list pages.
  // If we don't do this check, the sidebar will "flash" when
  // navigating from the list page to the individual sale page
  const salesCache = queryClient.getQueriesData<PaginatedSaleList>({
    queryKey: getOrdersQueryKeyPartial(),
  });
  // find the sale in the cache, and check its status
  for (const [, paginatedSaleList] of salesCache) {
    if (paginatedSaleList?.results) {
      for (const sale of paginatedSaleList.results) {
        if (sale.qid === String(qid)) {
          return getMappedTabForStatus(sale.status);
        }
      }
    }
  }

  return undefined;
}

export function SellerSidebar({
  validateIsActiveLink,
  onClickRoute,
}: SellerSidebarProps) {
  const user = useUserContext();

  const isInOrdersPath = validateIsActiveLink(/\/orders\//);

  const orderTabForQidPage = useActiveOrderTabForOrderQidPage(isInOrdersPath);

  const showInternalNavigationItems = Boolean(user?.isQogitaUser);

  const showInsights = useIsInsightsEnabled();

  return (
    <Navigation>
      <Navigation.Item
        as={Link}
        href="/dashboard"
        isActive={validateIsActiveLink(/\/dashboard\//)}
        onClick={onClickRoute}
      >
        <Navigation.Icon>
          <HomeIcon />
        </Navigation.Icon>
        Dashboard
      </Navigation.Item>

      <Navigation.Section isActive={isInOrdersPath}>
        <Navigation.SectionHeading>
          <Navigation.Icon>
            <ArchiveBoxIcon />
          </Navigation.Icon>
          Orders
        </Navigation.SectionHeading>

        <Navigation.SectionItems>
          <Navigation.Item
            as={Link}
            href={routes.orders('CURRENT', 1)}
            isActive={
              validateIsActiveLink(/\/orders\/\?status=CURRENT/) ||
              orderTabForQidPage === 'CURRENT'
            }
            onClick={onClickRoute}
          >
            Current orders
          </Navigation.Item>

          <Navigation.Item
            as={Link}
            href={routes.orders('COMPLETED', 1)}
            isActive={
              validateIsActiveLink(/\/orders\/\?status=COMPLETED/) ||
              orderTabForQidPage === 'COMPLETED'
            }
            onClick={onClickRoute}
          >
            Completed orders
          </Navigation.Item>

          <Navigation.Item
            as={Link}
            href={routes.orders('EXPIRED', 1)}
            isActive={
              validateIsActiveLink(/\/orders\/\?status=EXPIRED/) ||
              orderTabForQidPage === 'EXPIRED'
            }
            onClick={onClickRoute}
          >
            Expired orders
          </Navigation.Item>
        </Navigation.SectionItems>
      </Navigation.Section>

      <Navigation.Item
        as={Link}
        href={routes.inventory({ page: 1 })}
        isActive={validateIsActiveLink(/\/inventory\/\?/)}
        onClick={onClickRoute}
      >
        <Navigation.Icon>
          <ClipboardDocumentCheckIcon />
        </Navigation.Icon>
        Inventory
      </Navigation.Item>

      <Navigation.Item
        as={Link}
        href={routes.trendingProducts}
        isActive={validateIsActiveLink(/(\/trending-products\/)/)}
        onClick={onClickRoute}
      >
        <Navigation.Icon>
          <FireIcon />
        </Navigation.Icon>
        Trending products
      </Navigation.Item>

      {showInsights ? (
        <Navigation.Item
          as={Link}
          href={routes.insights}
          isActive={validateIsActiveLink(/(\/insights\/)/)}
          onClick={onClickRoute}
          data-dd-action-name="Insights Navigation Item"
        >
          <Navigation.Icon>
            <PresentationChartBarIcon />
          </Navigation.Icon>
          Insights
        </Navigation.Item>
      ) : null}

      {showInternalNavigationItems ? (
        <Navigation.Section
          isActive={validateIsActiveLink(
            /(\/internal\/|\/seller-initial-checker\/)/,
          )}
        >
          <Navigation.SectionHeading>
            <Navigation.Icon>
              <ChartBarIcon />
            </Navigation.Icon>
            Internal
          </Navigation.SectionHeading>

          <Navigation.SectionItems>
            <Navigation.Item
              as={Link}
              href="/internal/seller-initial-checker"
              isActive={validateIsActiveLink(
                /^\/internal\/seller-initial-checker/,
              )}
              onClick={onClickRoute}
            >
              Seller initial check
            </Navigation.Item>

            <Navigation.Item
              as={Link}
              href="/internal/seller-onboarding"
              onClick={onClickRoute}
              isActive={validateIsActiveLink(/^\/internal\/seller-onboarding/)}
            >
              Seller onboarding
            </Navigation.Item>

            {isProduction ? null : (
              <Navigation.Item
                as={Link}
                href="/internal/create-test-sale"
                onClick={onClickRoute}
                isActive={validateIsActiveLink(/^\/internal\/create-test-sale/)}
              >
                Create test sale
              </Navigation.Item>
            )}
          </Navigation.SectionItems>
        </Navigation.Section>
      ) : null}

      <Navigation.Item
        as={Link}
        href="/help-center"
        isActive={validateIsActiveLink(/\/help-center\//)}
        onClick={onClickRoute}
      >
        <Navigation.Icon>
          <QuestionMarkCircleIcon />
        </Navigation.Icon>
        Help center
      </Navigation.Item>
    </Navigation>
  );
}
